import { useCallback } from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import { toast } from 'sonner'
import type { DataPage, PowerPage } from '@/types/synoptique'

interface UsePageManagementParams {
  dataPages: DataPage[]
  setDataPages: (pages: DataPage[] | ((prev: DataPage[]) => DataPage[])) => void
  currentDataPageId: string
  setCurrentDataPageId: (id: string) => void
  powerPages: PowerPage[]
  setPowerPages: (pages: PowerPage[] | ((prev: PowerPage[]) => PowerPage[])) => void
  currentPowerPageId: string
  setCurrentPowerPageId: (id: string) => void
  editingPageId: string | null
  setEditingPageId: (id: string | null) => void
  editingPageName: string
  setEditingPageName: (name: string) => void
  editingPageType: 'data' | 'power' | null
  setEditingPageType: (type: 'data' | 'power' | null) => void
  demoMode: { state: { isDemo: boolean } }
  isPro: boolean
}

export function usePageManagement({
  dataPages,
  setDataPages,
  currentDataPageId,
  setCurrentDataPageId,
  powerPages,
  setPowerPages,
  currentPowerPageId,
  setCurrentPowerPageId,
  editingPageId,
  setEditingPageId,
  editingPageName,
  setEditingPageName,
  editingPageType,
  setEditingPageType,
  demoMode,
  isPro,
}: UsePageManagementParams) {
  const { t } = useLanguage()

  // ========== Data Pages ==========

  const addDataPage = useCallback(() => {
    if (demoMode.state.isDemo && !isPro) {
      toast.error(t('demo.pageCreationDisabled'))
      return
    }
    const newPageId = `data-${Date.now()}`
    const newPage: DataPage = {
      id: newPageId,
      name: `Data ${dataPages.length + 1}`,
      workspaceElements: [],
      cableRoutes: [],
      panelConnections: [],
      mireConnections: []
    }
    setDataPages([...dataPages, newPage])
    setCurrentDataPageId(newPageId)
  }, [dataPages, setDataPages, setCurrentDataPageId, demoMode, isPro, t])

  const deleteDataPage = useCallback((pageId: string) => {
    if (dataPages.length === 1) return
    const newPages = dataPages.filter(p => p.id !== pageId)
    setDataPages(newPages)
    if (currentDataPageId === pageId) {
      setCurrentDataPageId(newPages[0].id)
    }
  }, [dataPages, setDataPages, currentDataPageId, setCurrentDataPageId])

  const renameDataPage = useCallback((pageId: string, newName: string) => {
    setDataPages(dataPages.map(p => p.id === pageId ? { ...p, name: newName } : p))
  }, [dataPages, setDataPages])

  const moveDataPage = useCallback((fromIndex: number, toIndex: number) => {
    const newPages = [...dataPages]
    const [movedPage] = newPages.splice(fromIndex, 1)
    newPages.splice(toIndex, 0, movedPage)
    setDataPages(newPages)
  }, [dataPages, setDataPages])

  const duplicateDataPage = useCallback((pageId: string) => {
    const pageToDuplicate = dataPages.find(p => p.id === pageId)
    if (!pageToDuplicate) return

    const timestamp = Date.now()
    const newPageId = `data-${timestamp}`

    const oldToNewIdMap = new Map<string, string>()

    const newElements = pageToDuplicate.workspaceElements.map((elem, index) => {
      const newId = `${elem.type}-copy-${timestamp}-${index}`
      oldToNewIdMap.set(elem.id, newId)
      return { ...elem, id: newId }
    })

    // Remappe les IDs d'éléments ET les endpoints de cellules mire "imageId_cell_row_col"
    const remapEndpoint = (endpointId: string): string => {
      const direct = oldToNewIdMap.get(endpointId)
      if (direct) return direct
      const cellIdx = endpointId.lastIndexOf('_cell_')
      if (cellIdx > 0) {
        const remappedImageId = oldToNewIdMap.get(endpointId.slice(0, cellIdx))
        if (remappedImageId) return remappedImageId + endpointId.slice(cellIdx)
      }
      return endpointId
    }

    const newCables = pageToDuplicate.cableRoutes.map((cable, index) => ({
      ...cable,
      id: `cable-copy-${timestamp}-${index}`,
      from: remapEndpoint(cable.from),
      to: remapEndpoint(cable.to)
    }))

    const newPanelConnections = pageToDuplicate.panelConnections.map(conn => ({
      ...conn,
      panelId: oldToNewIdMap.get(conn.panelId) || conn.panelId,
      processorId: oldToNewIdMap.get(conn.processorId) || conn.processorId
    }))

    const newMireConnections = (pageToDuplicate.mireConnections || []).map(conn => ({
      ...conn,
      imageId: oldToNewIdMap.get(conn.imageId) || conn.imageId,
      processorId: oldToNewIdMap.get(conn.processorId) || conn.processorId
    }))

    const newPage: DataPage = {
      id: newPageId,
      name: `${pageToDuplicate.name} (copie)`,
      workspaceElements: newElements,
      cableRoutes: newCables,
      panelConnections: newPanelConnections,
      mireConnections: newMireConnections
    }

    setDataPages([...dataPages, newPage])
    setCurrentDataPageId(newPageId)
  }, [dataPages, setDataPages, setCurrentDataPageId])

  // ========== Power Pages ==========

  const addPowerPage = useCallback(() => {
    if (demoMode.state.isDemo && !isPro) {
      toast.error(t('demo.pageCreationDisabled'))
      return
    }
    const newPageId = `power-${Date.now()}`
    const newPage: PowerPage = {
      id: newPageId,
      name: `Power ${powerPages.length + 1}`,
      workspaceElements: [],
      cableRoutes: [],
      powerConnections: [],
      mirePowerConnections: []
    }
    setPowerPages([...powerPages, newPage])
    setCurrentPowerPageId(newPageId)
  }, [powerPages, setPowerPages, setCurrentPowerPageId, demoMode, isPro, t])

  const deletePowerPage = useCallback((pageId: string) => {
    if (powerPages.length === 1) return
    const newPages = powerPages.filter(p => p.id !== pageId)
    setPowerPages(newPages)
    if (currentPowerPageId === pageId) {
      setCurrentPowerPageId(newPages[0].id)
    }
  }, [powerPages, setPowerPages, currentPowerPageId, setCurrentPowerPageId])

  const renamePowerPage = useCallback((pageId: string, newName: string) => {
    setPowerPages(powerPages.map(p => p.id === pageId ? { ...p, name: newName } : p))
  }, [powerPages, setPowerPages])

  const movePowerPage = useCallback((fromIndex: number, toIndex: number) => {
    const newPages = [...powerPages]
    const [movedPage] = newPages.splice(fromIndex, 1)
    newPages.splice(toIndex, 0, movedPage)
    setPowerPages(newPages)
  }, [powerPages, setPowerPages])

  const duplicatePowerPage = useCallback((pageId: string) => {
    const pageToDuplicate = powerPages.find(p => p.id === pageId)
    if (!pageToDuplicate) return

    const timestamp = Date.now()
    const newPageId = `power-${timestamp}`

    const oldToNewIdMap = new Map<string, string>()

    const newElements = pageToDuplicate.workspaceElements.map((elem, index) => {
      const newId = `${elem.type}-copy-${timestamp}-${index}`
      oldToNewIdMap.set(elem.id, newId)
      return { ...elem, id: newId }
    })

    // Remappe les IDs d'éléments ET les endpoints de cellules mire "imageId_cell_row_col"
    const remapEndpoint = (endpointId: string): string => {
      const direct = oldToNewIdMap.get(endpointId)
      if (direct) return direct
      const cellIdx = endpointId.lastIndexOf('_cell_')
      if (cellIdx > 0) {
        const remappedImageId = oldToNewIdMap.get(endpointId.slice(0, cellIdx))
        if (remappedImageId) return remappedImageId + endpointId.slice(cellIdx)
      }
      return endpointId
    }

    const newCables = pageToDuplicate.cableRoutes.map((cable, index) => ({
      ...cable,
      id: `cable-copy-${timestamp}-${index}`,
      from: remapEndpoint(cable.from),
      to: remapEndpoint(cable.to)
    }))

    const newPowerConnections = pageToDuplicate.powerConnections.map(conn => ({
      ...conn,
      panelId: oldToNewIdMap.get(conn.panelId) || conn.panelId,
      cabinetId: oldToNewIdMap.get(conn.cabinetId) || conn.cabinetId
    }))

    const newMirePowerConnections = (pageToDuplicate.mirePowerConnections || []).map(conn => ({
      ...conn,
      imageId: oldToNewIdMap.get(conn.imageId) || conn.imageId,
      cabinetId: oldToNewIdMap.get(conn.cabinetId) || conn.cabinetId
    }))

    const newPage: PowerPage = {
      id: newPageId,
      name: `${pageToDuplicate.name} (copie)`,
      workspaceElements: newElements,
      cableRoutes: newCables,
      powerConnections: newPowerConnections,
      mirePowerConnections: newMirePowerConnections
    }

    setPowerPages([...powerPages, newPage])
    setCurrentPowerPageId(newPageId)
  }, [powerPages, setPowerPages, setCurrentPowerPageId])

  // ========== Generic Page Functions ==========

  const startEditingPage = useCallback((pageId: string, pageType: 'data' | 'power') => {
    const page = pageType === 'data'
      ? dataPages.find(p => p.id === pageId)
      : powerPages.find(p => p.id === pageId)
    if (page) {
      setEditingPageId(pageId)
      setEditingPageName(page.name)
      setEditingPageType(pageType)
    }
  }, [dataPages, powerPages, setEditingPageId, setEditingPageName, setEditingPageType])

  const finishEditingPage = useCallback(() => {
    if (editingPageId && editingPageName.trim()) {
      if (editingPageType === 'data') {
        renameDataPage(editingPageId, editingPageName.trim())
      } else {
        renamePowerPage(editingPageId, editingPageName.trim())
      }
    }
    setEditingPageId(null)
    setEditingPageName('')
  }, [editingPageId, editingPageName, editingPageType, renameDataPage, renamePowerPage, setEditingPageId, setEditingPageName])

  return {
    addDataPage,
    deleteDataPage,
    renameDataPage,
    moveDataPage,
    duplicateDataPage,
    addPowerPage,
    deletePowerPage,
    renamePowerPage,
    movePowerPage,
    duplicatePowerPage,
    startEditingPage,
    finishEditingPage,
  }
}
